import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";

import { CategoriesQuery, DepotsQuery } from "../near-miss.index/Query";
import { RaiseNearMissMutation } from "./Mutation";

const GENERIC_ERROR =
  "Could not create the near miss. Please try again, or contact support if it keeps happening.";

const emptyForm = {
  categoryId: "",
  depotId: "",
  location: "",
  description: "",
};

function NewNearMissButton() {
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(emptyForm);
  const [eventDate, setEventDate] = useState(moment());
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: categoriesData } = useQuery(CategoriesQuery, {
    fetchPolicy: "cache-and-network",
  });
  const { data: depotsData } = useQuery(DepotsQuery, {
    fetchPolicy: "cache-and-network",
  });

  const categoryConnection = categoriesData?.nearMissCategories?.all;
  const depotConnection = depotsData?.depots?.all;

  const [raiseNearMiss, { loading: saving }] = useMutation(
    RaiseNearMissMutation,
    {
      refetchQueries: ["NearMisses"],
    }
  );

  const handleOpenClick = () => {
    setValues(emptyForm);
    setEventDate(moment());
    setErrorMessage(null);
    setOpen(true);
  };

  const handleCancelClick = () => {
    setOpen(false);
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const isComplete =
    values.categoryId !== "" && values.depotId !== "" && eventDate?.isValid();

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!isComplete) return;
    setErrorMessage(null);

    const input = {
      categoryId: values.categoryId,
      depotId: values.depotId,
      location: values.location,
      description: values.description,
      eventDate: eventDate.format("YYYY-MM-DDTHH:mm:ssZ"),
    };

    raiseNearMiss({ variables: { input } })
      .then(({ data }) => {
        const payload = data?.nearMisses?.new;
        if (!payload) {
          setErrorMessage(GENERIC_ERROR);
          return;
        }

        const { error, nearMiss } = payload;

        if (error !== null) {
          setErrorMessage(`${error.message} (${error.code})`);
          return;
        }

        setOpen(false);
        history.push(`/near-miss/${nearMiss.id}`);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(GENERIC_ERROR);
      });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpenClick}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        New near miss
      </Button>

      <Dialog
        open={open}
        onClose={handleCancelClick}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: 720 } }}
        aria-labelledby="new-near-miss-dialog-title"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle id="new-near-miss-dialog-title">
            New near miss
          </DialogTitle>
          <DialogContent>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errorMessage}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 0, mb: 1 }}>
              <Grid xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="new-near-miss-category-label" required>
                    Category
                  </InputLabel>
                  <Select
                    labelId="new-near-miss-category-label"
                    id="new-near-miss-category"
                    label="Category"
                    disabled={saving}
                    value={values.categoryId}
                    onChange={(e) => handleChange("categoryId", e.target.value)}
                  >
                    {categoryConnection &&
                      categoryConnection.edges.map(({ node }) => (
                        <MenuItem key={node.id} value={node.id}>
                          {node.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="new-near-miss-depot-label" required>
                    Depot
                  </InputLabel>
                  <Select
                    labelId="new-near-miss-depot-label"
                    id="new-near-miss-depot"
                    label="Depot"
                    disabled={saving}
                    value={values.depotId}
                    onChange={(e) => handleChange("depotId", e.target.value)}
                  >
                    {depotConnection &&
                      depotConnection.edges.map(({ node }) => (
                        <MenuItem key={node.id} value={node.id}>
                          {node.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label="Event date"
                      value={eventDate}
                      onChange={(value) => setEventDate(value)}
                      disabled={saving}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <TextField
                  name="location"
                  label="Location"
                  placeholder="e.g. Loading bay 3"
                  autoComplete="off"
                  fullWidth
                  disabled={saving}
                  value={values.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                />
              </Grid>

              <Grid xs={12}>
                <TextField
                  name="description"
                  label="Description of near miss"
                  placeholder="What happened, and what could have gone wrong?"
                  multiline
                  rows={3}
                  fullWidth
                  disabled={saving}
                  value={values.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelClick} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !isComplete}>
              {saving ? "Creating" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default NewNearMissButton;
